<!-- nova-premier.component.html -->
<div class="nova-premier-dialog">
  <button
    class="close-btn"
    mat-icon-button
    (click)="close()"
    aria-label="Close Amazon Nova Lite Info"
  >
    ×
  </button>

  <div class="header">
    <img
      src="assets/logos/amazon.svg"
      alt="Amazon Nova Premier Logo"
      class="logo"
    />

    <h2>Amazon Nova Premier</h2>

    <div class="sub">
      Top-tier Nova model for maximum accuracy, deep reasoning, and
      enterprise-grade agent workflows
    </div>
  </div>

  <div class="body">
    <p>
      <strong>Amazon Nova Premier</strong> is AWS’s highest-capability Nova model,
      designed for workloads where you want the
      <strong>best reasoning quality</strong>, the strongest performance on
      <strong>complex, multi-step decisions</strong>, and
      <strong>reliable tool-driven execution</strong>.
      <br />
      If Lite is built for everyday scale and Pro is built for “do-more” accuracy,
      <strong>Premier</strong> is the model you reach for when tasks are
      <strong>high-stakes</strong>, inputs are <strong>messy</strong>, and outcomes must be
      <strong>auditable</strong>, <strong>stable</strong>, and <strong>production-safe</strong>.
    </p>

    <ul>
      <li>
        🧠 <strong>Maximum Reasoning Depth:</strong> Best choice when your workflow needs
        multi-hop thinking, constraint satisfaction, long-horizon planning, and “tradeoff”
        explanations that hold up under review.
      </li>
      <li>
        ✅ <strong>Highest Accuracy for Hard Inputs:</strong> Stronger on ambiguous prompts,
        contradictory documents, policy-heavy contexts, and edge cases where smaller models
        can drift or hallucinate.
      </li>
      <li>
        🧩 <strong>Agentic Workflow Excellence:</strong> Ideal for
        plan → tool call → verification → reporting loops, especially when you need
        deterministic structure (schemas/JSON) and robust follow-through.
      </li>
      <li>
        🧾 <strong>Long-Context Enterprise Work:</strong> Built for large policy sets,
        security controls, contracts, SOPs, audit logs, and knowledge-base dumps —
        while maintaining coherence and grounded answers.
      </li>
      <li>
        🖼️🎥📄 <strong>Multimodal Understanding:</strong> Supports text + image + video + document
        inputs for advanced document intelligence, visual QA, and “evidence-based”
        responses over charts/figures/screenshots.
      </li>
      <li>
        🌐 <strong>Built-in Tools:</strong> Supports web grounding (retrieval + citations)
        and code execution/interpretation for validating logic, calculations, and checks inside
        the same workflow.
      </li>
      <li>
        🔐 <strong>Enterprise Guardrails:</strong> Designed for governed environments with
        safety patterns appropriate for regulated or compliance-heavy domains.
      </li>
      <li>
        🧱 <strong>Bedrock-Native Deployment:</strong> Delivered through
        <strong>Amazon Bedrock</strong> with AWS-native security posture, access controls,
        and deployment options (including cross-Region inference where enabled).
      </li>
    </ul>

    <div class="optimal">
      <h3>When is Amazon Nova Premier optimal?</h3>
      <ul>
        <li>
          High-stakes decision support: compliance, finance, security operations,
          incident response, and policy interpretation
        </li>
        <li>
          Complex agent pipelines: planning + tool calls + validation + final report,
          where correctness is more important than lowest cost
        </li>
        <li>
          Deep document intelligence: contracts, legal review, risk registers,
          control frameworks, SOC2/ISO mappings, and audit-ready summaries
        </li>
        <li>
          Multimodal enterprise QA: screenshots, architecture diagrams, charts, product
          images, and video clips tied to a “show your work” narrative
        </li>
        <li>
          Structured outputs: stable JSON schemas and deterministic formatting for
          downstream systems and workflow automation
        </li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Recommended production pattern (Premier as an escalator)</h3>
      <ul>
        <li>
          Use <strong>Lite</strong> for routing, high-volume steps, quick drafts, and
          low-risk transformations
        </li>
        <li>
          Use <strong>Pro</strong> for most “serious” work: strong accuracy with balanced cost
        </li>
        <li>
          Escalate to <strong>Premier</strong> when risk is high: ambiguity, contradictions,
          multi-step plans, critical decisions, or strict policy constraints
        </li>
        <li>
        Make actions deterministic with <strong>tool calls</strong> (search/DB reads/ticket creation),
          then validate inputs server-side to prevent unsafe or hallucinated calls
        </li>
        <li>
          Add verifiers: schema validation, rule checks, and post-hoc “explain + cite” prompts
          for auditability
        </li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Where Premier tends to shine</h3>
      <ul>
        <li>
          🔎 <strong>Root-cause analysis:</strong> correlating logs, timelines, and changes into a defensible
          causal narrative and remediation plan
        </li>
        <li>
          🧠 <strong>Policy & compliance assistants:</strong> long-context + citations + consistent
          answers with minimal drift across runs
        </li>
        <li>
          🧑‍💻 <strong>Engineering copilots:</strong> larger context reading, patch planning, and “verify with tools/tests”
          workflows
        </li>
        <li>
          📊 <strong>Executive-ready deliverables:</strong> crisp decision briefs with tradeoffs,
          assumptions, and recommended defaults
        </li>
      </ul>
    </div>
  </div>
</div>
